/**
 * =============================================================================
 *  THAO TAC TRANG - TRANG THAI VA LENH, TACH KHOI CHO NO DUOC VE RA
 * =============================================================================
 *
 *  Truoc day toan bo phan nay nam trong ui/PagePanel.tsx, tuc la trong thanh
 *  ben. Nguoi dung yeu cau dua "Xoay trái / Xoay phải / Xóa" va nhom "Trang" ra
 *  THANH CONG CU TREN, trong khi anh thu nho va viec CHON trang van o thanh
 *  ben. Hai khung hinh, mot trang thai.
 *
 *  Neu de moi ben tu giu lua chon cua rieng no thi thanh cong cu se xoay mot
 *  tap trang khac voi tap dang sang len trong thanh ben - dung loai "ban xem
 *  truoc noi doi" ma ca du an nay duoc viet ra de chan. Nen lua chon song o
 *  DUNG MOT CHO: hook nay, do App goi, va ca hai khung hinh cung doc.
 *
 * =============================================================================
 *  BA CHO DE SAI, GIU NGUYEN TU BAN CU
 * =============================================================================
 *
 *  1. XOA NHIEU TRANG PHAI DI TU DUOI LEN. Lenh 'page.remove' mang mot chi so,
 *     va xoa trang 2 lam moi trang phia sau tut mot bac. Xoa [1,3,5] theo thu
 *     tu tang dan se xoa nham hai trang cuoi. Sap xep GIAM DAN thi moi chi so
 *     con lai deu chua bi dong toi.
 *
 *  2. KIEM TRA CA LO TRUOC KHI PHAT LENH NAO. Neu lenh thu ba trong lo khong
 *     hop le, ta khong duoc de hai lenh dau da nam trong lich su: nguoi dung se
 *     co mot thao tac lam DO DANG ma khong hieu vi sao.
 *
 *  3. KIEM TRA BANG DUNG HAM MA DUONG LUU DUNG. applyPageCommand() o day va
 *     applyPageCommand() trong PdfCoreBrowser.save() la MOT ham. Neu cho nay
 *     dung mot ban kiem tra rieng, hai ban se troi khoi nhau va thanh ben se
 *     hua mot thu tu trang ma tep xuat ra khong co.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DocumentId, EditCommand, PdfCore } from '@pdfeditor/types';

import { store } from '../state';
import type { SelectMode } from './Sidebar';
import type { MergePreviewFile } from './PageToolbar';
import { baseNameOf, downloadPdfBytes } from './download';
import { parsePageRange } from '../core/pages/ranges';
import {
  PAGE_OPS_FIDELITY_WARNING,
  applyPageCommand,
  type PageCommand,
  type PagePlan,
} from '../core/pages/plan';
import { extractPages, splitIntoSinglePages } from '../core/pages/operations';
import { asPageOpsCore, type RegisteredSource } from '../core/pages/capability';
import { pageActionState, type PageActionGate, type PageActionState } from './toolbarMenus';

/** Tren nguong nay, tach tung trang se sinh ra qua nhieu lan tai ve mot luc. */
const SPLIT_CONFIRM_THRESHOLD = 20;

export interface UsePageOpsArgs {
  core: PdfCore | null;
  doc: DocumentId | null;
  plan: PagePlan;
  fileName: string | null;
  buildCurrentPdf: () => Promise<Uint8Array | null>;
}

export interface PageOps {
  totalPages: number;
  selected: ReadonlySet<number>;
  busy: boolean;
  capable: boolean;

  onSelectSlot: (slotIndex: number, mode: SelectMode) => void;
  selectAll: () => void;
  clearSelection: () => void;

  rangeText: string;
  rangeError: string | null;
  setRangeText: (text: string) => void;
  applyRange: () => void;

  rotate: (delta: 90 | 270) => void;
  remove: () => void;
  extract: () => void;
  insertBlank: () => void;
  splitAll: () => void;
  reorder: (from: number, to: number) => void;

  mergePreview: MergePreviewFile[] | null;
  pickMergeFiles: (files: File[]) => void;
  confirmMerge: () => void;
  cancelMerge: () => void;

  warning: string | null;
  dismissWarning: () => void;

  sourceLabel: (docId: string) => string | null;

  /** Cua chan da tinh san - dung chung cho thanh cong cu va thanh ben. */
  gate: (action: Parameters<typeof pageActionState>[0]) => PageActionState;
}

export function usePageOps({ core, doc, plan, fileName, buildCurrentPdf }: UsePageOpsArgs): PageOps {
  const pageOps = useMemo(() => asPageOpsCore(core), [core]);

  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const anchorRef = useRef<number | null>(null);
  const [rangeText, setRangeText] = useState('');
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [warningShown, setWarningShown] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState<
    { preview: MergePreviewFile[]; sources: RegisteredSource[] } | null
  >(null);

  // Doi tai lieu: moi thu ve trang thai ban dau, KE CA canh bao. Canh bao la
  // "mot lan cho moi tai lieu", khong phai "mot lan cho moi phien lam viec".
  useEffect(() => {
    setSelected(new Set());
    anchorRef.current = null;
    setRangeText('');
    setRangeError(null);
    setWarningDismissed(false);
    setWarningShown(false);
    setMergeCandidates(null);
  }, [doc?.id]);

  // Trang bi xoa thi bo khoi danh sach chon: giu lai mot chi so khong con ton
  // tai la duong dan thang toi mot lenh tro nham trang.
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((i) => i < plan.length));
      return next.size === prev.size ? prev : next;
    });
  }, [plan.length]);

  // --- Chon trang ----------------------------------------------------------

  const handleSelectSlot = useCallback((slotIndex: number, mode: SelectMode) => {
    setRangeError(null);
    setSelected((prev) => {
      if (mode === 'toggle') {
        const next = new Set(prev);
        if (next.has(slotIndex)) next.delete(slotIndex);
        else next.add(slotIndex);
        anchorRef.current = slotIndex;
        return next;
      }
      if (mode === 'range' && anchorRef.current !== null) {
        const lo = Math.min(anchorRef.current, slotIndex);
        const hi = Math.max(anchorRef.current, slotIndex);
        const next = new Set<number>();
        for (let i = lo; i <= hi; i++) next.add(i);
        return next;
      }
      anchorRef.current = slotIndex;
      return new Set([slotIndex]);
    });
  }, []);

  const applyRange = useCallback(() => {
    const parsed = parsePageRange(rangeText, plan.length);
    if (!parsed.ok) {
      setRangeError(parsed.error);
      return;
    }
    setRangeError(null);
    setSelected(new Set(parsed.indices));
    anchorRef.current = parsed.indices[0] ?? null;
  }, [rangeText, plan.length]);

  const selectAll = useCallback(() => {
    setSelected(new Set(plan.map((_, i) => i)));
    anchorRef.current = 0;
  }, [plan]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    anchorRef.current = null;
  }, []);

  // --- Phat lenh -----------------------------------------------------------

  /**
   * Kiem tra CA LO tren mot ban sao cua so do, roi moi phat.
   *
   * Ap thu lan luot dung ham ma duong luu dung, nen mot lo duoc chap nhan o day
   * chac chan phat lai duoc o buoc luu.
   */
  const dispatchBatch = useCallback(
    (commands: PageCommand[], successMessage: string): boolean => {
      if (commands.length === 0) return false;

      let probe: PagePlan = plan;
      for (const command of commands) {
        const step = applyPageCommand(probe, command);
        if (!step.ok) {
          store.notify(step.error, 'error');
          return false;
        }
        probe = step.plan;
      }

      for (const command of commands) store.dispatch(command as EditCommand);

      if (!warningShown) {
        setWarningShown(true);
        setWarningDismissed(false);
      }
      store.notify(successMessage, 'info');
      return true;
    },
    [plan, warningShown],
  );

  const rotate = useCallback(
    (delta: 90 | 270) => {
      // Xoay khong doi chi so trang nao, nen thu tu trong lo khong quan trong.
      const commands: PageCommand[] = [...selected]
        .sort((a, b) => a - b)
        .map((pageIndex) => ({ kind: 'page.rotate', pageIndex, delta }));
      dispatchBatch(
        commands,
        `Đã xoay ${commands.length} trang ${delta === 90 ? 'sang phải' : 'sang trái'}.`,
      );
    },
    [selected, dispatchBatch],
  );

  const remove = useCallback(() => {
    // GIAM DAN. Xem ghi chu (1) o dau tep: xoa tu tren xuong se xoa nham trang.
    const commands: PageCommand[] = [...selected]
      .sort((a, b) => b - a)
      .map((pageIndex) => ({ kind: 'page.remove', pageIndex }));
    if (dispatchBatch(commands, `Đã xóa ${commands.length} trang.`)) clearSelection();
  }, [selected, dispatchBatch, clearSelection]);

  const insertBlank = useCallback(() => {
    const after = selected.size > 0 ? Math.max(...selected) : plan.length - 1;
    const atIndex = Math.min(plan.length, after + 1);
    dispatchBatch(
      [{ kind: 'page.insert', atIndex, source: { kind: 'blank' } }],
      `Đã chèn trang trắng ở vị trí ${atIndex + 1}.`,
    );
  }, [selected, plan.length, dispatchBatch]);

  const reorder = useCallback(
    (from: number, to: number) => {
      if (dispatchBatch([{ kind: 'page.reorder', from, to }], `Đã chuyển trang ${from + 1} → ${to + 1}.`)) {
        setSelected(new Set([to]));
        anchorRef.current = to;
      }
    },
    [dispatchBatch],
  );

  // --- Trich xuat / tach ---------------------------------------------------

  const extract = useCallback(async () => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    try {
      const bytes = await buildCurrentPdf();
      if (!bytes) return;
      const picked = [...selected].sort((a, b) => a - b);
      const out = await extractPages(bytes, picked);
      downloadPdfBytes(out, `${baseNameOf(fileName)}-trich-${picked.length}-trang.pdf`);
      store.notify(`Đã trích xuất ${picked.length} trang ra tệp riêng.`, 'info');
    } catch (err) {
      store.notify(`Không trích xuất được: ${String(err).slice(0, 160)}`, 'error');
    } finally {
      setBusy(false);
    }
  }, [selected, busy, buildCurrentPdf, fileName]);

  const splitAll = useCallback(async () => {
    if (busy || plan.length === 0) return;
    if (plan.length > SPLIT_CONFIRM_THRESHOLD) {
      const proceed = window.confirm(
        `Thao tác này sẽ tải về ${plan.length} tệp, mỗi tệp một trang.\n\n` +
          'Trình duyệt có thể hỏi bạn cho phép tải nhiều tệp cùng lúc.\n\nTiếp tục?',
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      const bytes = await buildCurrentPdf();
      if (!bytes) return;
      const parts = await splitIntoSinglePages(bytes);
      const base = baseNameOf(fileName);
      const pad = String(parts.length).length;
      parts.forEach((part, i) => {
        downloadPdfBytes(part, `${base}-trang-${String(i + 1).padStart(pad, '0')}.pdf`);
      });
      store.notify(`Đã tách thành ${parts.length} tệp.`, 'info');
    } catch (err) {
      store.notify(`Không tách được: ${String(err).slice(0, 160)}`, 'error');
    } finally {
      setBusy(false);
    }
  }, [busy, plan.length, buildCurrentPdf, fileName]);

  // --- Ghep tep ------------------------------------------------------------

  /**
   * Buoc 1: doc va PHAN TICH tung tep, chua dong vao tai lieu dang lam viec.
   *
   * Mot tep hong chi lam hong CHINH NO. Bao ten tep va ly do, giu nguyen cac
   * tep con lai - huy ca lan ghep vi mot tep xau la bat nguoi dung lam lai tu
   * dau ma khong duoc gi.
   */
  const pickMergeFiles = useCallback(
    async (files: File[]) => {
      if (!pageOps || busy) return;
      setBusy(true);
      try {
        const preview: MergePreviewFile[] = [];
        const sources: RegisteredSource[] = [];

        for (const file of files) {
          if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
            preview.push({ name: file.name, pageCount: null, error: 'Không phải tệp PDF.' });
            continue;
          }
          try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const registered = await pageOps.registerSourceDocument(bytes, file.name);
            if (!registered.ok) {
              preview.push({ name: file.name, pageCount: null, error: registered.error.userMessage });
              continue;
            }
            preview.push({ name: file.name, pageCount: registered.value.pageCount, error: null });
            sources.push(registered.value);
          } catch (err) {
            preview.push({
              name: file.name,
              pageCount: null,
              error: `Không đọc được tệp: ${String(err).slice(0, 80)}`,
            });
          }
        }

        setMergeCandidates({ preview, sources });
      } finally {
        setBusy(false);
      }
    },
    [pageOps, busy],
  );

  /** Buoc 2: noi tung trang cua tung tep vao cuoi so do. */
  const confirmMerge = useCallback(() => {
    const candidates = mergeCandidates;
    if (!candidates) return;

    const commands: PageCommand[] = [];
    let at = plan.length;
    for (const source of candidates.sources) {
      for (let i = 0; i < source.pageCount; i++) {
        commands.push({
          kind: 'page.insert',
          atIndex: at,
          source: { kind: 'document', docId: source.docId, pageIndex: i },
        });
        at += 1;
      }
    }

    const totalPages = commands.length;
    if (dispatchBatch(commands, `Đã ghép ${candidates.sources.length} tệp (${totalPages} trang).`)) {
      setMergeCandidates(null);
    }
  }, [mergeCandidates, plan.length, dispatchBatch]);

  // --- Ket noi -------------------------------------------------------------

  const sourceLabel = useCallback(
    (docId: string) => pageOps?.sourceLabel(docId) ?? null,
    [pageOps],
  );

  const capable = pageOps !== null && doc !== null;
  const gateInput: PageActionGate = {
    capable,
    busy,
    totalPages: plan.length,
    selectedCount: selected.size,
  };
  const gate = useCallback(
    (action: Parameters<typeof pageActionState>[0]) => pageActionState(action, gateInput),
    // Chi bon con so nay quyet dinh ket qua; nho chung thay vi ca doi tuong de
    // moi lan ve lai khong sinh ra mot ham moi ma khong co gi doi.
    [capable, busy, plan.length, selected.size],
  );

  return {
    totalPages: plan.length,
    selected,
    busy,
    capable,
    onSelectSlot: handleSelectSlot,
    selectAll,
    clearSelection,
    rangeText,
    rangeError,
    setRangeText,
    applyRange,
    rotate,
    remove,
    extract: () => void extract(),
    insertBlank,
    splitAll: () => void splitAll(),
    reorder,
    mergePreview: mergeCandidates?.preview ?? null,
    pickMergeFiles: (files) => void pickMergeFiles(files),
    confirmMerge,
    cancelMerge: () => setMergeCandidates(null),
    warning: warningShown && !warningDismissed ? PAGE_OPS_FIDELITY_WARNING : null,
    dismissWarning: () => setWarningDismissed(true),
    sourceLabel,
    gate,
  };
}
